import { LESSONS, languageLessons, LANGUAGES, SUBJECTS, subjectLessons, type RaceResult, type UserStats } from '@typing-racer/shared';
import { clear, h, ordinal, pct } from '../dom.js';
import { barChart, lineChart } from './charts.js';

const ALL_LESSONS = [...LESSONS, ...LANGUAGES.flatMap(languageLessons), ...SUBJECTS.flatMap(subjectLessons)];
const lessonTitle = (id: string) => ALL_LESSONS.find((l) => l.id === id)?.title ?? id;
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

/** Coaching lines: what is holding you back and what changing it would be worth. */
export function coachingLines(stats: UserStats, latest?: RaceResult): { title: string; body: string }[] {
  const out: { title: string; body: string }[] = [];
  const s = stats.summary;
  if (latest) {
    const dw = latest.summary.wpm - s.avgWpm;
    const da = latest.summary.accuracy - s.avgAccuracy;
    if (s.races > 1) {
      out.push({
        title: `This race: ${Math.round(latest.summary.wpm)} WPM at ${pct(latest.summary.accuracy)}`,
        body:
          `${dw >= 0 ? '+' : ''}${dw.toFixed(1)} WPM against your average of ${s.avgWpm}, ` +
          `accuracy ${da >= 0 ? 'up' : 'down'} ${Math.abs(Math.round(da * 100))} points on your usual ${pct(s.avgAccuracy)}.`,
      });
    }
    if (latest.summary.errors > 0) {
      const saved = latest.summary.errors * 0.9;
      const secs = Math.max(1, latest.summary.durationMs / 1000);
      // Speed you would have had over the shorter race; capped so tiny races don't produce silly figures.
      const gain = Math.min(latest.summary.wpm * 0.5, latest.summary.wpm * (saved / secs));
      out.push({
        title: `${latest.summary.errors} typo${latest.summary.errors === 1 ? '' : 's'} cost you about ${saved.toFixed(0)} seconds`,
        body: `Each typo needs a Backspace and a re-type, roughly 0.9 s. With zero typos you would have finished around ${saved.toFixed(0)} s sooner, worth about ${gain.toFixed(0)} WPM. Accuracy is the cheapest speed there is.`,
      });
    }
  }
  if (s.races >= 10) {
    const trend = s.recentWpm - s.earlierWpm;
    out.push({
      title: trend >= 1 ? `You are getting faster: +${trend.toFixed(1)} WPM` : trend <= -1 ? `Speed has dipped by ${Math.abs(trend).toFixed(1)} WPM` : 'Speed is steady',
      body: `Your last five races average ${s.recentWpm} WPM, the five before that ${s.earlierWpm} WPM. Accuracy moved from ${pct(s.earlierAccuracy)} to ${pct(s.recentAccuracy)}.`,
    });
  }
  for (const hnt of stats.recurringHints.slice(0, 2)) {
    if (hnt.count >= 2) {
      out.push({
        title: `Recurring: ${hnt.title}`,
        body: `This came up in ${hnt.count} of your last ${hnt.races} races. Fixing one recurring habit is usually worth more than 5 WPM. Try the matching typing lesson and re-check this screen after a few races.`,
      });
    }
  }
  const worst = stats.keyStats.filter((k) => k.errors / k.count >= 0.08).slice(0, 3);
  if (worst.length) {
    out.push({
      title: `Weak keys: ${worst.map((k) => (k.key === ' ' ? 'Space' : `"${k.key}"`)).join(', ')}`,
      body: `You mistype ${worst.map((k) => `${k.key === ' ' ? 'Space' : k.key} ${Math.round((k.errors / k.count) * 100)}%`).join(', ')} of the time. Slow down on those specific keys for a few races; accuracy there will come back as speed.`,
    });
  }
  if (out.length === 0) out.push({ title: 'Keep racing', body: 'A few more races and this screen will show what to work on.' });
  return out;
}

export function renderStats(root: HTMLElement, stats: UserStats, onBack: () => void): void {
  clear(root);
  const s = stats.summary;
  const arrow = (recent: number, earlier: number, up = true) => (s.races < 10 ? '' : recent > earlier ? (up ? ' ↑' : ' ↑') : recent < earlier ? ' ↓' : ' →');
  const tile = (v: string, l: string) => h('div', { class: 'stat' }, h('div', { class: 'v' }, v), h('div', { class: 'l' }, l));
  const races = stats.races;
  const wpmPts = races.map((r, i) => ({ x: i, y: r.wpm, label: `${fmtDate(r.playedAt)}: ${Math.round(r.wpm)} WPM, ${pct(r.accuracy)}, ${ordinal(r.place)} of ${r.players}${r.mode === 'practice' ? ' (practice)' : ''}` }));
  const accPts = races.map((r, i) => ({ x: i, y: r.accuracy * 100, label: `${fmtDate(r.playedAt)}: ${pct(r.accuracy)} accuracy at ${Math.round(r.wpm)} WPM` }));
  const weak = stats.keyStats.slice(0, 8).map((k) => ({ label: k.key, value: (k.errors / k.count) * 100, detail: `${k.errors} typos in ${k.count} presses, median ${k.medianMs} ms` }));
  const slow = [...stats.keyStats].filter((k) => k.medianMs > 0).sort((a, b) => b.medianMs - a.medianMs).slice(0, 8).map((k) => ({ label: k.key, value: k.medianMs, detail: `${k.count} presses` }));
  const lessonsDone = new Map<string, number>();
  for (const l of stats.lessons) lessonsDone.set(l.lessonId, Math.max(lessonsDone.get(l.lessonId) ?? 0, l.stars));

  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'panel wide stack' },
        h('div', { class: 'row', style: 'justify-content:space-between' }, h('h1', {}, `📈 ${stats.name}'s progress`), h('button', { class: 'btn small ghost', onclick: onBack }, '← Back')),
        h(
          'div',
          { class: 'row' },
          tile(String(s.races), 'Races'),
          tile(`${Math.round(s.avgWpm)}${arrow(s.recentWpm, s.earlierWpm)}`, 'Avg WPM'),
          tile(String(Math.round(s.bestWpm)), 'Best WPM'),
          tile(`${pct(s.avgAccuracy)}${arrow(s.recentAccuracy, s.earlierAccuracy)}`, 'Accuracy'),
          tile(String(s.lessons), 'Lessons'),
        ),
        h('h2', {}, '🧭 What to work on'),
        coachingLines(stats).map((c) => h('div', { class: 'hint' }, h('b', {}, c.title), c.body)),
        h('div', { class: 'charts' },
          lineChart(wpmPts, { colour: '#1e88e5', title: 'Words per minute, race by race', format: (v) => String(Math.round(v)) }),
          lineChart(accPts, { colour: '#43a047', title: 'Accuracy, race by race', yMin: Math.min(80, ...accPts.map((p) => Math.floor(p.y))), yMax: 100, format: (v) => `${Math.round(v)}%` }),
          barChart(weak, { colour: '#e53935', title: 'Most mistyped keys (error rate)', format: (v) => `${v.toFixed(0)}%` }),
          barChart(slow, { colour: '#fb8c00', title: 'Slowest keys (median ms)', format: (v) => `${Math.round(v)} ms` }),
        ),
        stats.lessons.length
          ? h(
              'div',
              {},
              h('h2', {}, '📚 Lessons'),
              h('div', { class: 'row small' }, [...lessonsDone.entries()].map(([id, st]) => h('span', { class: 'badge', style: 'background:#fff8d6' }, `${lessonTitle(id)} ${'★'.repeat(st)}`))),
            )
          : null,
      ),
    ),
  );
}
